/**
 * A caixa "Conferência — Importar Guia DAE (Outros Bancos)".
 *
 * Papel: reproduz `#modal-dae-gap`, `abrirConferenciaDaeGap` e
 * `confirmarImportacaoDaeGap` (l. 895–923, 4586–4649): a faixa azul com o nome
 * do favorecido, o período de apuração lido, uma linha por código da guia com o
 * valor editável, e "Confirmar Importação", que guarda os itens marcados no
 * cartão — sem criar lançamento e sem tocar o saldo do C6.
 *
 * Como usar: quem monta já leu o PDF e só abre a caixa quando há linha; a caixa
 * fecha sozinha depois de gravar. A guia vai para o **mês aberto**, e não para
 * o período de apuração dela, como na origem, e substitui a que o cartão já
 * tinha naquele mês.
 */
import { type Favorecido, competenciaComBarra } from '@concierge/shared';
import { useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import {
  type LinhaDaConferencia,
  linhasConfirmadas,
  linhasDaConferencia,
} from '../conferencia';
import { useGravarGuiaDeOutrosBancos } from '../hooks/usar-financas-extras';
import { type GuiaDoPdf } from '../importacao/guia-dae';
import { CaixaInformativa } from './kit/aviso-de-conferencia';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import { Campo, LinhaDeCampos } from './kit/campo';
import { Celula } from './kit/tabela';
import { TabelaDeConferencia } from './tabela-de-conferencia';

interface ConferenciaDaGuiaDeOutrosBancosProps {
  /** O que o parser leu do PDF, com ao menos uma linha. */
  guia: GuiaDoPdf;
  /** O favorecido do cartão que pediu a importação. */
  favorecido: Favorecido;
  /** O mês aberto, em `AAAA-MM`, para onde a guia vai. */
  competencia: string;
  onFechar: () => void;
}

/**
 * O texto de uma falha de gravação.
 *
 * @param erro - O que a mutação lançou.
 * @returns A mensagem da API, que nunca traz dado da família.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * A conferência da guia DAE de um cartão de Outros Bancos.
 *
 * @param guia - As linhas, o período e o total lidos do PDF.
 * @param favorecido - O favorecido do cartão, cujo nome a faixa azul mostra.
 * @param competencia - O mês aberto.
 * @param onFechar - Fecha a caixa: o "✕", o véu, `Esc`, "Cancelar" e a
 *   gravação que deu certo.
 * @returns A caixa de 720 px.
 */
export function ConferenciaDaGuiaDeOutrosBancos({
  guia,
  favorecido,
  competencia,
  onFechar,
}: ConferenciaDaGuiaDeOutrosBancosProps) {
  const notificar = useNotificacao();
  const gravar = useGravarGuiaDeOutrosBancos();
  const [linhas, setLinhas] = useState<LinhaDaConferencia[]>(() =>
    linhasDaConferencia(guia.itens.map((item) => item.valor)),
  );

  /** "Confirmar Importação" (l. 4614–4649). */
  async function handleConfirmar() {
    const itens = linhasConfirmadas(linhas).flatMap(({ indice, valor }) => {
      const item = guia.itens[indice];
      return item === undefined
        ? []
        : [
            {
              codigo: item.codigo,
              denominacao: item.denominacao,
              subcategoria: item.subcategoria,
              valor,
            },
          ];
    });
    if (itens.length === 0) {
      notificar('Nenhum item selecionado.', false);
      return;
    }
    try {
      await gravar.mutateAsync({
        mes: { favorecidoId: favorecido.id, competencia },
        corpo: { competencia: guia.competencia, itens },
      });
      onFechar();
      notificar(
        `✓ Guia DAE importada (${String(itens.length)} ${itens.length > 1 ? 'itens' : 'item'}) — só informativo, não afeta o saldo do C6`,
      );
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  return (
    <CaixaDeDialogo
      titulo="Conferência — Importar Guia DAE (Outros Bancos)"
      largura={720}
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void handleConfirmar()} disabled={gravar.isPending}>
            Confirmar Importação
          </Botao>
        </>
      }
    >
      <CaixaInformativa>
        Esta guia é só informativa: entra na folha e no fechamento de{' '}
        <strong>{favorecido.nome}</strong>, mas não cria lançamento nem afeta o saldo do C6 (é
        paga por fora, pela conta apartada).
      </CaixaInformativa>
      <LinhaDeCampos colunas={2} className="mb-1.5">
        <Campo rotulo="Período de Apuração" id="dae-gap-competencia">
          <div id="dae-gap-competencia" className="py-1.5 font-semibold text-foreground">
            {guia.competencia === null ? '—' : competenciaComBarra(guia.competencia)}
          </div>
        </Campo>
      </LinhaDeCampos>
      <div className="mb-2.5 text-[11px] text-texto-fraco">
        Revise cada linha da guia antes de confirmar. Desmarque quem não deve entrar. O PDF em
        si não é salvo no sistema — apenas os itens abaixo, e somente ao confirmar.
      </div>
      <TabelaDeConferencia
        colunas={['Denominação', 'Subcategoria']}
        colunaDoValor="Valor (R$)"
        linhas={linhas}
        rotuloDaLinha={(indice) => guia.itens[indice]?.denominacao ?? ''}
        celulas={(indice) => (
          <>
            <Celula>
              {guia.itens[indice]?.denominacao}
              <div className="text-[11px] text-texto-fraco">
                Código {guia.itens[indice]?.codigo}
              </div>
            </Celula>
            <Celula className="text-[12px]">{guia.itens[indice]?.subcategoria}</Celula>
          </>
        )}
        totalPdf={guia.totalPdf}
        onMudar={setLinhas}
      />
    </CaixaDeDialogo>
  );
}
